/**
 * @file Represents the PageLayout component which offers a consistent structure
 * to the pages by providing a navigation bar at the top and a footer at the bottom.
 */
import { Container, styled } from '@mui/material';
import React from 'react';
import PropTypes from 'prop-types';

import { NavBar } from '../components/bars/NavBar';

const StyledLayout = styled('div')(({ theme }) => ({
    display: 'flex',
    minHeight: '100vh'
}));

const Content = styled('div')(({ theme }) => ({
    flexGrow: 1,
    width: '100%', // Ensure content takes up the full width
    overflow: 'visible' // This line ensures that content can be scrolled if it exceeds its height
}));

const StyledContainer = styled(Container)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('sm')]: {
        //for mobile the background colour is different
        backgroundColor: theme.palette.background.panel
    }
}));

/** @component
 * Provides a consistent layout structure for pages, including a navigation bar at the top and a footer at the bottom.
 *
 * @param {Object} props - The properties for the PageLayout component.
 * @param {React.ReactNode} props.children - The main content to be rendered within the layout.
 * @returns {React.ReactElement} Returns the layout structure containing the provided content.
 */
export const PageLayout = ({ children }) => {
    return (
        <StyledLayout id='pageLayout'>
            <NavBar />
            <StyledContainer
                id='pageLayoutContainer'
                maxWidth='100%'
                style={{
                    paddingRight: 0,
                    paddingLeft: 0
                }}
            >
                <Content>{children}</Content>
            </StyledContainer>
        </StyledLayout>
    );
};

PageLayout.propTypes = {
    children: PropTypes.node.isRequired
};
